import { Link } from 'react-router-dom';
import { AlertCircle, Check } from 'lucide-react';

import { ScrollReveal, SplitReveal } from '@/components/motion';
import { INTERNSHIP_DISCLAIMER, INTERNSHIP_PATHWAYS } from '@/data/experience';
import { Button } from '@ibas/ui';

/**
 * InternshipProgramme (/experience — section 4) — "Turn study into
 * international experience". The three INTERNSHIP_PATHWAYS (Remote / Global /
 * On-site) render as a ScrollReveal-staggered grid of plain hover-lift cards:
 * each carries its icon in a soft navy chip, the pathway name, a gold-uppercase
 * audience label, the description, and its supporting points as a Check-marked
 * list (tick in text-brand-gold — never white-on-gold). The mandatory
 * INTERNSHIP_DISCLAIMER is reproduced verbatim in a muted note strip beneath
 * the grid, and a single navy CTA points to /admissions.
 *
 * Motion: SplitReveal headline (by lines), ScrollReveal child-stagger for the
 * pathway grid. Plain text cards (never TiltCard) so nothing glares away on
 * hover. All primitives are reduced-motion safe and never strand content
 * hidden. Sourcing: every fact comes from '@/data/experience'; no placement is
 * promised anywhere.
 */
export function InternshipProgramme() {
  return (
    <section className="border-t border-border bg-muted/30 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Internship programme
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Turn study into international experience.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              Three routes let you gain professional experience alongside your studies — remotely,
              internationally, or on-site in Switzerland — each open to a different group of
              students.
            </p>
          </ScrollReveal>
        </header>

        {/* ── The three pathways ──────────────────────────────────────── */}
        <ScrollReveal
          stagger={0.1}
          className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3"
        >
          {INTERNSHIP_PATHWAYS.map((pathway) => (
            <div
              key={pathway.name}
              className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                <pathway.Icon aria-hidden className="size-6" />
              </div>
              <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                {pathway.name}
              </h3>
              <p className="mt-2 font-label text-xs tracking-[0.18em] text-brand-gold uppercase">
                {pathway.audience}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {pathway.description}
              </p>
              <ul className="mt-5 flex flex-col gap-2.5 border-t border-border pt-5">
                {pathway.points.map((point) => (
                  <li key={point} className="flex items-start gap-2.5">
                    <Check aria-hidden className="mt-0.5 size-4 shrink-0 text-brand-gold" />
                    <span className="text-sm leading-relaxed text-foreground">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </ScrollReveal>

        {/* ── Mandatory compliance disclaimer (verbatim) ──────────────── */}
        <ScrollReveal from="up" delay={0.1}>
          <div className="mt-10 flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-5">
            <AlertCircle aria-hidden className="mt-0.5 size-5 shrink-0 text-brand-navy" />
            <p className="text-sm leading-relaxed text-muted-foreground">
              {INTERNSHIP_DISCLAIMER}
            </p>
          </div>
        </ScrollReveal>

        {/* ── CTA ─────────────────────────────────────────────────────── */}
        <ScrollReveal from="up" delay={0.15}>
          <div className="mt-9">
            <Button asChild variant="primary" size="lg" data-cursor>
              <Link to="/admissions">Explore admissions</Link>
            </Button>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
